/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Eye, EyeOff } from 'lucide-react';

import { useChat } from '@/components/providers/ChatProvider';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  deleteGrafanaConfiguration,
  type GrafanaConfiguration,
  monitoringGrafanaQueries,
  upsertGrafanaConfiguration,
} from '@/lib/queries/integrations/monitoring/grafana';
import type { Project } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

import {
  convertToBackendPayload,
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  initialValue?: GrafanaConfiguration;
  mainProject: Project;
}

export const GrafanaForm = ({
  initialValue,
  mainProject,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const { setPrompt } = useChat();
  const router = useRouter();
  const { toast, dismiss } = useToast();
  const [showToken, setShowToken] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      // Convert backend data to form data
      ...(initialValue && {
        deploymentType: initialValue.isSelfHosted ? 'self-hosted' : 'cloud',
        instanceUrl: initialValue.instanceUrl,
        serviceAccountDisplayName: initialValue.serviceAccountDisplayName,
        serviceAccountToken: initialValue.serviceAccountToken,
      }),
    },
  });

  const deploymentType = form.watch('deploymentType');

  const { isPending, mutateAsync } = useMutation({
    mutationKey: generateMutationKey(
      monitoringGrafanaQueries.byProject(mainProject.id),
      'upsert',
      true,
    ),
    mutationFn: async (values: FormValues) => {
      // Convert frontend form data to backend payload
      const backendPayload = convertToBackendPayload(values);
      await upsertGrafanaConfiguration(
        mainProject.id,
        backendPayload,
        initialValue?.id,
      );
    },
    onSuccess: async () => {
      toast({
        title:
          initialValue !== undefined
            ? 'integrations.monitoring.grafana.form.update.success'
            : 'integrations.monitoring.grafana.form.create.success',
        variant: 'default',
        description: 'integrations.monitoring.grafana.tryIt',
        descriptionValues: {
          button: parts => (
            <>
              <br />
              <Button
                variant="main"
                className="mt-2"
                onClick={() => {
                  dismiss();
                  setPrompt(
                    'What are the current alerts in my Grafana instance?',
                  );
                }}
              >
                {parts}
              </Button>
            </>
          ),
        },
      });
      router.navigate({
        to: '/integrations/monitoring/grafana',
      });
    },
    onError: () => {
      toast({
        title:
          initialValue !== undefined
            ? 'integrations.monitoring.grafana.form.update.error'
            : 'integrations.monitoring.grafana.form.create.error',
        variant: 'destructive',
      });
    },
  });

  const { isPending: isDeleting, mutateAsync: deleteMutation } = useMutation({
    mutationKey: generateMutationKey(
      monitoringGrafanaQueries.list(mainProject.id),
      'delete',
      true,
    ),
    mutationFn: async (configurationId: string) => {
      await deleteGrafanaConfiguration(mainProject.id, configurationId);
    },
    onSuccess: async () => {
      toast({
        title: 'integrations.monitoring.grafana.form.delete.success',
        variant: 'default',
      });
      router.navigate({
        to: '/integrations/monitoring/grafana',
      });
    },
    onError: () => {
      toast({
        title: 'integrations.monitoring.grafana.form.delete.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(async values => mutateAsync(values))}>
        <h1 className="text-4xl mb-3">
          <FormattedMessage id="integrations.monitoring.grafana.form.title" />
        </h1>
        <h3 className="text-lg mb-6">
          <FormattedMessage id="integrations.monitoring.grafana.form.subtitle" />
        </h3>
        <div className="flex flex-col gap-6 w-full max-w-md mb-5">
          {/* Deployment Type */}
          <FormField
            control={form.control}
            name="deploymentType"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.monitoring.grafana.form.deploymentType.label" />
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={formatMessage({
                          id: 'integrations.monitoring.grafana.form.deploymentType.placeholder',
                        })}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="cloud">
                      <FormattedMessage id="integrations.monitoring.grafana.form.deploymentType.cloud" />
                    </SelectItem>
                    <SelectItem value="self-hosted">
                      <FormattedMessage id="integrations.monitoring.grafana.form.deploymentType.selfHosted" />
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Instance URL - unified field for both deployment types */}
          <FormField
            control={form.control}
            name="instanceUrl"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.monitoring.grafana.form.instanceUrl.label" />
                </FormLabel>
                <FormControl>
                  {deploymentType === 'cloud' ? (
                    <div className="flex items-center">
                      <Input
                        value={field.value
                          .replace('https://', '')
                          .replace('.grafana.net', '')}
                        onChange={e => {
                          const instanceName = e.target.value;
                          field.onChange(
                            instanceName !== ''
                              ? `${instanceName}.grafana.net`
                              : '',
                          );
                        }}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-l-lg focus:ring-primary-600 focus:border-primary-600 block flex-1 h-10 px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        type="text"
                        placeholder={formatMessage({
                          id: 'integrations.monitoring.grafana.form.instanceUrl.cloud.placeholder',
                        })}
                      />
                      <span className="bg-gray-100 border border-l-0 border-gray-300 text-gray-500 h-10 flex items-center px-3 rounded-r-lg dark:bg-gray-600 dark:border-gray-600 dark:text-gray-400">
                        .grafana.net
                      </span>
                    </div>
                  ) : (
                    <Input
                      {...field}
                      className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      type="url"
                      placeholder={formatMessage({
                        id: 'integrations.monitoring.grafana.form.instanceUrl.selfHosted.placeholder',
                      })}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Service Account Display Name */}
          <FormField
            control={form.control}
            name="serviceAccountDisplayName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.monitoring.grafana.form.serviceAccountDisplayName.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="text"
                    placeholder={formatMessage({
                      id: 'integrations.monitoring.grafana.form.serviceAccountDisplayName.placeholder',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Service Account Token */}
          <FormField
            control={form.control}
            name="serviceAccountToken"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.monitoring.grafana.form.serviceAccountToken.label" />
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      type={showToken ? 'text' : 'password'}
                      placeholder={formatMessage({
                        id: 'integrations.monitoring.grafana.form.serviceAccountToken.placeholder',
                      })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-row gap-4 justify-between">
            {initialValue !== undefined ? (
              <>
                <Button variant="main" disabled={isPending} type="submit">
                  {isPending ? (
                    <FormattedMessage id="integrations.monitoring.grafana.form.update.pending" />
                  ) : (
                    <FormattedMessage id="integrations.monitoring.grafana.form.update.label" />
                  )}
                </Button>
                <Button
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600"
                  disabled={isDeleting}
                  onClick={() => {
                    deleteMutation(initialValue.id);
                  }}
                  type="button"
                >
                  {isDeleting ? (
                    <FormattedMessage id="integrations.monitoring.grafana.form.delete.pending" />
                  ) : (
                    <FormattedMessage id="integrations.monitoring.grafana.form.delete.label" />
                  )}
                </Button>
              </>
            ) : (
              <Button variant="main" disabled={isPending} type="submit">
                {isPending ? (
                  <FormattedMessage id="integrations.monitoring.grafana.form.create.pending" />
                ) : (
                  <FormattedMessage id="integrations.monitoring.grafana.form.create.label" />
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};
